import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Method, Prop, Watch } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { DebounceDelays } from '../../shared/debounce-constants';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';
import { debounce } from '../../utils/debounce';
import { getRandomId } from '../../utils/get-random-id';
import { isBaseCharsEqual } from '../../utils/is-base-chars-equal';
import { querySelectorAllAsArray } from '../../utils/query-selector-all-as-array';
import { connectTabAndContent } from './tabs.util';
import { SharedDirectionService } from '../../global/workbench-global';

/**
 * @slot - wb-tab elements
 * @slot content - optional wb-tab-content elements
 */
@Component({
  tag: 'wb-tabs',
  styleUrl: 'tabs.scss',
  shadow: true,
})
export class TabsComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /**
   * Colour scheme
   * @deprecated Use `data-on` attribute instead
   * */
  @Prop({ reflect: true })
  public theme: WorkbenchTheme;

  /** namespace for element and all its children */
  @Prop({ reflect: true })
  public namespace: string = getRandomId();

  /** name of the selected tab */
  @Prop({ reflect: true, mutable: true })
  public selected: string;

  /** apply to make wb-tab elements take up all available space */
  @Prop({ reflect: true })
  public fitted: boolean = false;

  /** sets aria-label for the button that scrolls towards the end */
  @Prop()
  public scrollToEndButtonTitle: string = 'scroll to end';

  /** sets aria-label for the button that scrolls towards the start */
  @Prop()
  public scrollToStartButtonTitle: string = 'scroll to start';

  /** @internal */
  @Prop({ reflect: true, mutable: true })
  public isRtl: boolean = false;

  /** Event emitted when selected tab has changed */
  @Event()
  public wbchange: EventEmitter<string>;

  private TagNames = transformTagNames(this, 'wb-tabs', ['wb-tab', 'wb-tab-content', 'wb-horizontal-scroll']);

  private highlightEl: HTMLElement;
  private tabEls: Array<HTMLWbTabElement> = [];
  private tabContentEls: Array<HTMLWbTabContentElement> = [];
  private listEl: HTMLElement;

  /** @internal called by child <wb-tab> to highlight first tab on tabs changed */
  @Method()
  public async updateTabsHighlight(): Promise<void> {
    this.debounceUpdateHighlight();
  }

  @Watch('selected')
  public onSelectedChange(newSelected: string, prevSelected: string): void {
    // fix for #2881: wait for potential dynamically rendered wb-tab elements to be rendered. otherwise we would early return
    requestAnimationFrame(() => {
      const hasUserClearedSelection = prevSelected && !newSelected;

      if (hasUserClearedSelection) {
        this.reset();
        return;
      }

      const newSelectedTabEl = this.tabEls.find((el) => isBaseCharsEqual(el.name, newSelected));

      // change nothing, when user tries to select disabled or non-existing tab
      if (!newSelectedTabEl || newSelectedTabEl.disabled) {
        return;
      }

      this.selected = newSelectedTabEl.name;
      this.wbchange.emit(this.selected);
      this.updateChildElements();
    });
  }

  private updateChildElements(): void {
    this.tabEls.forEach((el: HTMLWbTabElement) => (el.selected = isBaseCharsEqual(el.name, this.selected)));
    this.tabContentEls.forEach((el) => (el.selected = isBaseCharsEqual(el.name, this.selected)));
    this.updateHighlightedTab();
  }

  @Listen('click')
  public onClick(event: MouseEvent): void {
    const composedPath = event.composedPath();
    const tabEl = composedPath.find((el: HTMLElement) => el.tagName && el.tagName.match(new RegExp(`^${this.TagNames.WbTab}$`, 'i'))) as HTMLWbTabElement;

    if (tabEl && !tabEl.disabled) {
      this.selected = tabEl.name;
    }
  }

  @Listen('resize', { target: 'window' })
  public onResize(): void {
    this.debounceUpdateHighlight();
  }

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection() {
    this.isRtl = SharedDirectionService.isRtl;
  }

  public connectedCallback(): void | Promise<void> {
    if (this.selected === '') {
      // if selected attribute is present but empty, select the first tab
      this.setTabEls();
      this.selected = this.tabEls[0]?.name;
    }
  }

  public componentDidLoad(): void {
    this.connectTabsAndContents();

    if (this.selected) {
      this.updateChildElements();
    }
  }

  private connectTabsAndContents(): void {
    this.tabEls.forEach((tabEl) => {
      const tabContentEl = this.tabContentEls.find((tabContentEl) => isBaseCharsEqual(tabContentEl.name, tabEl.name));

      if (tabContentEl) {
        connectTabAndContent(this.namespace, tabEl, tabContentEl);
      }
    });
  }

  private reset(): void {
    // note: this.selected is always null/undefined at this point
    this.resetTabBar();
    this.tabEls.forEach((el: HTMLWbTabElement) => (el.selected = false));
    this.tabContentEls.forEach((el) => (el.selected = false));
    this.wbchange.emit(this.selected);
  }

  private resetTabBar(): void {
    this.highlightEl.style.width = '0px';
  }

  private updateHighlightedTab(): void {
    const selectedElement = this.tabEls.find((el) => isBaseCharsEqual(el.name, this.selected));
    // workaround for core#1396
    if (!this.highlightEl || !selectedElement) {
      this.resetTabBar();
      return;
    }

    const offsetDiff = this.isRtl ? this.el.offsetWidth - this.listEl.offsetWidth : 0;
    const offsetLeft = selectedElement.offsetLeft - offsetDiff;

    this.highlightEl.style[this.isRtl ? 'insetInlineEnd' : 'insetInlineStart'] = `${offsetLeft}px`;
    this.highlightEl.style[!this.isRtl ? 'insetInlineEnd' : 'insetInlineStart'] = 'initial';
    this.highlightEl.style.width = `${selectedElement.offsetWidth}px`;
  }

  private debounceUpdateHighlight = debounce(() => this.updateHighlightedTab(), DebounceDelays.Resize);

  private bindHighlightRef = (el: HTMLDivElement): void => {
    this.highlightEl = el;
  };

  private bindListRef = (el: HTMLElement): void => {
    this.listEl = el;
  };

  private setTabEls = (): void => {
    this.tabEls = querySelectorAllAsArray(this.el, this.TagNames.WbTab);
  };

  private updateTabEls = (): void => {
    this.setTabEls();
    this.debounceUpdateHighlight();
  };

  private updateTabContentEls = (): void => {
    this.tabContentEls = querySelectorAllAsArray(this.el, this.TagNames.WbTabContent);
  };

  private setRef = (el: HTMLElement): void => {
    el.addEventListener('wbchange', (evt: CustomEvent) => {
      evt.stopPropagation(); // stop wbchange event propagation from content
    });
  };

  public render(): JSX.Element {
    const { WbHorizontalScroll } = this.TagNames;

    return (
      <Host class="wb-tabs" data-on={getDataOnFromTheme(this.el, this.theme)}>
        <div class="inner">
          <WbHorizontalScroll //
            scrollToEndButtonTitle={this.scrollToEndButtonTitle}
            scrollToStartButtonTitle={this.scrollToStartButtonTitle}
          >
            <div role="tablist" class="wrapper" ref={this.bindListRef}>
              <slot onSlotchange={this.updateTabEls} />
              <div class="highlight" ref={this.bindHighlightRef} />
            </div>
          </WbHorizontalScroll>
        </div>
        <slot name="content" onSlotchange={this.updateTabContentEls} ref={this.setRef} />
      </Host>
    );
  }
}
